export type MapCenter = {latitude: number; longitude: number};

/** Periods a signed-out visitor can pick on the public Explore+ map (WSDB caps it at 7 days). */
export const PUBLIC_EXPLORE_DAY_OPTIONS = [1, 3, 7] as const;

/**
 * Reads `?lat=..&lng=..` (also `lon`) from a query string. Returns null when either is missing,
 * not a number, out of range, or 0,0.
 */
export const readMapCenterFromQuery = (search: string): MapCenter | null => {
  const params = new URLSearchParams(search || "");
  const lat = params.get("lat");
  const lng = params.get("lng") ?? params.get("lon");
  if (lat === null || lng === null || lat.trim() === "" || lng.trim() === "") {
    return null;
  }
  const latitude = Number(lat);
  const longitude = Number(lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180 || (latitude === 0 && longitude === 0)) {
    return null;
  }
  return {latitude, longitude};
};

/** Reads `?days=` and keeps it to one of the public options; anything else gives 7. */
export const readPublicExploreDays = (search: string): number => {
  const days = Number(new URLSearchParams(search || "").get("days"));
  return (PUBLIC_EXPLORE_DAY_OPTIONS as readonly number[]).includes(days) ? days : 7;
};

/** Reads `?sport=` as a raw sport type (0 or more); anything else means all sports. */
export const readSportFromQuery = (search: string): number | null => {
  const raw = new URLSearchParams(search || "").get("sport");
  if (raw === null || raw.trim() === "") {
    return null;
  }
  const sport = Number(raw);
  return Number.isInteger(sport) && sport >= 0 ? sport : null;
};

const AFTER_SIGN_IN_KEY = "ws-after-sign-in";
const AFTER_SIGN_IN_TTL_MS = 60 * 60 * 1000;

/** Only the signed-in Explore+ map is a valid place to return to after sign-in. */
const isSafeReturnPath = (path: unknown): path is string =>
  typeof path === "string" && /^\/around-me(?:[?#]|$)/.test(path);

/** Remembers where to go after sign-in (for this tab, one hour). */
export const rememberAfterSignIn = (path: string): void => {
  if (!isSafeReturnPath(path)) {
    return;
  }
  try {
    sessionStorage.setItem(AFTER_SIGN_IN_KEY, JSON.stringify({path, at: Date.now()}));
  } catch {
    // Storage blocked: sign-in still works, it just lands on the default page.
  }
};

/** The remembered return path, or null. Does not clear it: `clearAfterSignIn` does. */
export const readAfterSignIn = (now: number = Date.now()): string | null => {
  try {
    const raw = sessionStorage.getItem(AFTER_SIGN_IN_KEY);
    if (!raw) {
      return null;
    }
    const {path, at} = JSON.parse(raw);
    if (!isSafeReturnPath(path) || typeof at !== "number" || now - at > AFTER_SIGN_IN_TTL_MS) {
      return null;
    }
    return path;
  } catch {
    return null;
  }
};

export const clearAfterSignIn = (): void => {
  try {
    sessionStorage.removeItem(AFTER_SIGN_IN_KEY);
  } catch {
    // ignore
  }
};
